import { formatDate } from "@angular/common";
import { AttendanceStatus, SessionOutcome, StudentAttendance, TutorCancelledDialogResult } from "./leap-v2-attendance.model";
import { LeapV2Student, LeapV2StudentProgress } from "./leap-v2-student.model";
import { EvidenceFileDto } from "./leap-v2-api.model";

/**
 * The full session-report the screen works with, mirroring the API response shape
 * (GET api/Session/SessionReport/{placementId}/{orbitSessionId}) after adaptation to front-end types
 * (Date objects, string ids, behavioural concerns folded into each student's progress).
 */
export interface LeapV2SessionReport {
  sessionId: number;
  placementId: number;
  // Orbit BookingSessions.Id from the route — the session's identity. Required on every save payload:
  // the API refuses a Session save without it and a Report save silently matches no rows (it scopes its
  // UPDATE by OrbitSessionId). Stamped from the route after each load; not part of the GET response shape.
  orbitSessionId: number;
  session: LeapV2Session;
  report: LeapV2Report;
  tutorCancellation: TutorCancelledDialogResult | null;
  behaviouralConcerns: LeapV2BehaviouralConcern[];
  // The most recent rejection (reason / who / when). Null when never rejected. The API keeps returning it
  // after a resubmit, so the screen shows it only while session.status is 'Rejected'.
  rejection: LeapV2SessionRejection | null;
}

export interface LeapV2SessionRejection {
  reason: string;
  rejectedBy: string;
  rejectedDate: Date;
}

/**
 * Response of POST api/Session/SessionReport/Session (Form 1) and .../Report (Form 2). Only a report submit
 * settles anything: there `outcome` is what the server decided from the saved register and `status` the
 * session's new status — always 'Submitted', even when `outcome` is 'Cancelled'. A report submit produces a
 * report for staff to Accept/Reject whatever it did to the Orbit session, so it never lands on 'Cancelled';
 * that status is reserved for a session with no settled report (a tutor cancellation, or a coordinator's
 * Orbit diary cancel). A register submit always returns AwaitReport / 'Open'; a report draft returns
 * AwaitReport with no status. 'OrbitUnchanged' is a submit that left Orbit as it is: an earlier submit already
 * set the session's Orbit status, or payroll already held a session the register cancels. Any pay or bill
 * change is a session adjustment in Orbit.
 */
export interface LeapV2SessionSaveResponse {
  outcome: SessionOutcome | 'OrbitUnchanged';
  status: string;
  sessionId: number;
}

/** The "session" node of the response: attendance + session meta. */
export interface LeapV2Session {
  placementId: number;
  date: Date;
  startTime: string;
  endTime: string;
  subject: string;
  sessionType: string;
  deliveryMode: string;
  status: string;
  attendance: StudentAttendance[];
  attendanceSubmitted: boolean;
  consultantNotes: string;
  notesLastUpdated: Date;
  /** The delivery setting saved for this session (39883); null until chosen, and while the switch is off. */
  deliverySetting: string | null;
  /** The setting on the booking's most recent earlier session, offered as this one's default (39883). */
  previousDeliverySetting: string | null;
}

/** The "report" node of the response: learning objectives + per-student progress. */
export interface LeapV2Report {
  placementId: number;
  date: Date;
  students: LeapV2Student[];
  objectives: string[];
  /** How this session was delivered (39883), one of DELIVERY_SETTINGS. Saved with the report; null until chosen. */
  deliverySetting: string | null;
}


export interface LeapV2SessionAttendanceSubmit {
  placementId: number,
  orbitSessionId: number,
  // Sent as a date-only 'yyyy-MM-dd' string, not a Date: a Date is serialised via toISOString() (UTC),
  // which shifts the calendar day for any non-UTC user and lands a different StartDate than the one stored.
  date: string,
  startTime: string,
  endTime: string,
  subject: string,
  sessionType: string,
  deliveryMode: string,
  status: string,
  attendance: StudentAttendance[] ,
  attendanceSubmitted: true
}

export interface LeapV2SessionReportSubmit {
  placementId: number,
  orbitSessionId: number,
  // Date-only 'yyyy-MM-dd' string — see LeapV2SessionAttendanceSubmit.date for why a Date is not sent.
  date: string,
  students: LeapV2SessionReportStudentSubmit[],
  objectives: string[],
  submitted: boolean,
  // The tutor's delivery setting (39883). The API ignores it while the sessions.delivery-setting switch is off.
  deliverySetting: string | null,
  // Each learner's evidence is every file the panel shows, so the API may remove a stored file missing from it.
  // Without it the API only adds, which keeps a browser holding an older page from removing saved files (40454).
  evidenceIsComplete: boolean
}

/** A student as posted to the Report endpoint: evidence goes as DTOs, a newly picked file with its content in base64. */
export type LeapV2SessionReportStudentSubmit = Omit<LeapV2Student, 'progress'> & {
  progress: Omit<LeapV2StudentProgress, 'evidence'> & { evidence: EvidenceFileDto[] };
};

/**
 * Raw API response for GET api/Session/SessionReport/{placementId}/{orbitSessionId}. The backend returns
 * a wrapper (session + report + tutorCancellation + behaviouralConcerns); the service adapter flattens it
 * into the component's LeapV2Session. `session`/`report` are null when no session has been saved yet.
 */
export interface LeapV2SessionReportResponse {
  sessionId: number;
  placementId: number;
  session: LeapV2SessionForm | null;
  report: LeapV2ReportForm | null;
  // Keyed on orbitSessionId, not the LeapDb anchor the rest of this response uses — see the
  // tutorCancellation() call in leap-v2-session.service.ts (bug 37020).
  tutorCancellation: { bookingId: number; orbitSessionId: number; reason: string; comments: string } | null;
  behaviouralConcerns: LeapV2BehaviouralConcern[];
  rejection: { reason: string; rejectedBy: string; rejectedDate: string } | null;
}

export interface LeapV2SessionForm {
  placementId: number;
  date: string;
  startTime: string;
  endTime: string;
  subject: string;
  sessionType: string;
  deliveryMode: string;
  status: string;
  attendance: LeapV2AttendanceRow[];
  attendanceSubmitted: boolean;
  consultantNotes: string;
  notesLastUpdated: string | null;
  deliverySetting?: string | null;
  previousDeliverySetting?: string | null;
}

export interface LeapV2AttendanceRow {
  studentId: number;
  status: AttendanceStatus;
  reason?: string;
  comments?: string;
  parentCarerInformed?: string;
}

export interface LeapV2ReportForm {
  placementId: number;
  date: string;
  students: LeapV2ReportStudent[];
  objectives: string[];
}

export interface LeapV2ReportStudent {
  id: number;
  name: string;
  progress: {
    attainmentLevels: number[];
    objectiveComments: string;
    engagement: number;
    engagementComments: string;
    evidence: { name: string; size: number; contentType?: string; url?: string }[];
  };
}

export interface LeapV2BehaviouralConcern {
  studentId: number;
  bookingId: number;
  sessionId: number;
  reason: string;
  comments: string;
}

/**
 * The six per-session delivery settings (39883, Mike, 22 Oct 2026), in the order the session report lists them. The
 * wording is the wire value: it must match DeliverySettings in TutorialPortal.Entities/SessionReports exactly.
 */
export const DELIVERY_SETTINGS: readonly string[] = [
  'In person at home',
  'In person at a community setting',
  'In person at school',
  'Online at home',
  'Online at school',
  'Online at a community setting',
];

// The app pins LOCALE_ID to en-GB (app.module.ts); mirrored here so a pure function needs no injector.
const SESSION_LOCALE = 'en-GB';

/**
 * "12 May 3:30pm" — the session's date and start time as a single human label, for the Finalise session
 * confirm title. `startTime` is the API's 24h 'HH:mm' string; Angular has no lowercase am/pm token, so the
 * time part is lower-cased after formatting. Either part is dropped when missing, so a brand-new session
 * with no persisted date still gets a sensible title.
 */
export function formatSessionMoment(date: Date | null, startTime: string): string {
  const parts: string[] = [];
  if (date) {
    parts.push(formatDate(date, 'd MMM', SESSION_LOCALE));
  }
  const match = /^(\d{1,2}):(\d{2})/.exec(startTime ?? '');
  if (match) {
    const moment = new Date(date ?? 0);
    moment.setHours(Number(match[1]), Number(match[2]), 0, 0);
    parts.push(formatDate(moment, 'h:mma', SESSION_LOCALE).toLowerCase());
  }
  return parts.join(' ');
}
